import type { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import { useMemo } from 'react';
import type { BuildServerQuery, FetchServerRows } from './types';

type UseServerSideDatasourceArgs<
  TQuery,
  TRow,
  TUrlFilter extends Record<string, unknown>,
  TFilterModel extends Record<string, unknown>,
> = {
  urlFilter: TUrlFilter;
  buildQuery: BuildServerQuery<TQuery, TUrlFilter, TFilterModel>;
  fetchRows: FetchServerRows<TQuery, TRow>;
  onRowCountChange?: (filtered: number, total: number) => void;
};

/**
 * Composes an AG Grid server-side datasource from:
 * - `buildQuery`: maps AG Grid request + URL state into API query params
 * - `fetchRows`: executes API request and returns `{ rows, total }`
 *
 * Keeps feature datasources focused on domain query mapping while centralizing
 * `getRows` success/fail wiring and count propagation.
 */
export function useServerSideDatasource<
  TQuery,
  TRow,
  TUrlFilter extends Record<string, unknown> = Record<string, unknown>,
  TFilterModel extends Record<string, unknown> = Record<string, unknown>,
>({
  urlFilter,
  buildQuery,
  fetchRows,
  onRowCountChange,
}: UseServerSideDatasourceArgs<TQuery, TRow, TUrlFilter, TFilterModel>): IServerSideDatasource {
  return useMemo(
    () => ({
      getRows: (params: IServerSideGetRowsParams<TRow>) => {
        const request = params.request;
        const query = buildQuery({
          startRow: request.startRow ?? 0,
          endRow: request.endRow ?? (request.startRow ?? 0) + 100,
          sortModel: request.sortModel ?? [],
          filterModel: (request.filterModel ?? {}) as TFilterModel,
          urlFilter,
        });

        fetchRows(query)
          .then(({ rows, total }) => {
            onRowCountChange?.(total, total);
            params.success({
              rowData: rows,
              rowCount: total,
            });
          })
          .catch(() => {
            params.fail();
          });
      },
    }),
    [buildQuery, fetchRows, onRowCountChange, urlFilter]
  );
}
